// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + inner white surfaces with soft blue border
// - Inputs: soft blue border + orange focus
// - Tables: blue header, soft zebra rows, hover tint
// - IconButtons: blue default, orange on hover
// - No API/business logic changed

import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Divider,
  MenuItem,
  CircularProgress,
  Button,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { API_URL, API_UPLOAD_URL } from "./config";

const CampusEvents = () => {
  // ✅ Theme constants (same palette as your sample)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  // ===== Original state/logic =====
  const [data, setData] = useState([]);
  const [newRow, setNewRow] = useState({
    CampusCode: "",
    SubCampus: "",
    Title: "",
    Description: "",
    EventDate: "",
    Time: "",
    Location: "",
    Organizer: "",
    EventType: "",
    PhotoURL: "",
    SeatsCount: "",
    EventFlyer: "",
  });
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [campusCode, setCampusCode] = useState("");
  const [uploadingFlyerId, setUploadingFlyerId] = useState(null);

  const timeStrToDayjs = (t) => (t ? dayjs(`2000-01-01T${t}`) : null);
  const dayjsToTimeStr = (d) => (d && d.isValid() ? d.format("HH:mm") : "");

  const token = localStorage.getItem("auth");

  // decode campusCode
  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode);
      setNewRow((r) => ({ ...r, CampusCode: decoded.campusCode }));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, []);

  // central fetch fn
  const fetchEvents = async () => {
    if (!campusCode) return;
    try {
      const res = await axios.get(`${API_URL}/api/CampusEvents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      setError("Failed to fetch events");
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [campusCode]);

  const handleInputChange = (e, id = null) => {
    const { name, value } = e.target;
    if (id === null) {
      setNewRow((r) => ({ ...r, [name]: value }));
    } else {
      setData((d) => d.map((row) => (row.id === id ? { ...row, [name]: value } : row)));
    }
  };

  const handleAddRow = async () => {
    try {
      await axios.post(`${API_URL}/api/CampusEvents`, newRow, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      setNewRow({
        CampusCode: campusCode,
        Subcampus: "",
        Title: "",
        Description: "",
        EventDate: "",
        Time: "",
        Location: "",
        Organizer: "",
        EventType: "",
        PhotoURL: "",
        SeatsCount: "",
        EventFlyer: "",
      });
      await fetchEvents();
    } catch {
      setError("Failed to add event");
    }
  };

  const handleUpdateRow = async (id) => {
    try {
      const updated = data.find((r) => r.id === id);
      await axios.put(`${API_URL}/api/CampusEvents/${id}`, updated, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditId(null);
      setSuccess(true);
      await fetchEvents();
    } catch {
      setError("Failed to update event");
    }
  };

  const handleDeleteRow = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/CampusEvents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(true);
      await fetchEvents();
    } catch {
      setError("Failed to delete event");
    }
  };

  const handleFlyerUpload = async (e, id) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      setError("Only JPG, JPEG, and PNG images are allowed.");
      return;
    }

    setUploadingFlyerId(id);
    const formData = new FormData();
    formData.append("eventFlyer", file);
    formData.append("CampusCode", campusCode);
    if (id !== "new") {
      formData.append("EventID", id);
    }

    try {
      const res = await axios.post(`${API_URL}/api/uploadEventFlyer`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const filename = res.data.filename;
      
      if (id === "new") {
        setNewRow((r) => ({ ...r, EventFlyer: filename }));
      } else {
        setData((d) =>
          d.map((row) => (row.id === id ? { ...row, EventFlyer: filename } : row))
        );
      }
      setSuccess(true);
    } catch (err) {
      setError("Failed to upload flyer.");
      console.error(err);
    } finally {
      setUploadingFlyerId(null);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageWrapSx}>
        <Paper elevation={5} sx={rootPaperSx}>
          {/* ✅ Orange header bar */}
          <Box sx={headerSx}>
            <Box>
              <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
                Campus Events
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
                Create, edit, and manage campus event schedule
              </Typography>
            </Box>
          </Box>

          <Box sx={bodySx}>
            {!!error && (
              <Alert
                severity="error"
                onClose={() => setError("")}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  border: "1px solid rgba(244,67,54,0.25)",
                  backgroundColor: "rgba(244,67,54,0.04)",
                }}
              >
                {error}
              </Alert>
            )}

            {!!success && (
              <Snackbar open autoHideDuration={3000} onClose={() => setSuccess(false)}>
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Operation successful!
                </Alert>
              </Snackbar>
            )}

            {/* OUTER – keeps rounded corners & spacing */}
            <Paper elevation={0} sx={tableWrapSx}>

              {/* INNER – ONLY for horizontal scroll */}
              <Box
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                }}
              >
                <TableContainer component="div">
                  <Table size="small" sx={{ minWidth: 1500 }}>
                    <colgroup>
                      <col style={{ width: "2%" }} />
                      <col style={{ width: "6%" }} />
                      <col style={{ width: "6%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "6%" }} />
                      <col style={{ width: "16%" }} />
                      <col style={{ width: "9%" }} />
                      <col style={{ width: "6%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "8%" }} />
                    </colgroup>

                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        {["#", "Campus", "Subcampus", "Title", "Description", "Date", "Time", "Location", "Organizer", "Type", "Photo URL", "Seats Count", "EventFlyer", "Actions"].map(
                          (h) => (
                            <TableCell key={h}>{h}</TableCell>
                          )
                        )}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {data.map((row, i) =>
                        editId === row.id ? (
                          <TableRow key={row.id} sx={zebraRowSx(i)}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{row.CampusCode}</TableCell>

                            <TableCell>
                              <TextField
                                select
                                name="Subcampus"
                                value={row.Subcampus || ""}
                                onChange={(e) => handleInputChange(e, row.id)}
                                SelectProps={{ displayEmpty: true }}
                                size="small"
                                fullWidth
                                sx={inputSx}
                              >
                                <MenuItem value="" disabled>Subcampus</MenuItem>
                                <MenuItem value="K1">K1</MenuItem>
                                <MenuItem value="KK">KK</MenuItem>
                                <MenuItem value="KW">KW</MenuItem>
                              </TextField>
                            </TableCell>

                            <TableCell>
                              <TextField
                                name="Title"
                                value={row.Title}
                                onChange={(e) => handleInputChange(e, row.id)}
                                size="small"
                                fullWidth
                                sx={inputSx}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                name="Description"
                                value={row.Description}
                                onChange={(e) => handleInputChange(e, row.id)}
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                sx={inputSx}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                type="date"
                                name="EventDate"
                                value={row.EventDate?.substr(0, 10) || ""}
                                onChange={(e) => handleInputChange(e, row.id)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                fullWidth
                                sx={inputSx}
                              />
                            </TableCell>

                            <TableCell>
                              <TimePicker
                                value={timeStrToDayjs((row.Time || "").substr(0, 5))}
                                onChange={(val) => {
                                  const timeStr = dayjsToTimeStr(val);
                                  setData((d) => d.map((r) => (r.id === row.id ? { ...r, Time: timeStr } : r)));
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    size: "small",
                                    sx: inputSx,
                                    placeholder: "Time",
                                  },
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                name="Location"
                                value={row.Location}
                                onChange={(e) => handleInputChange(e, row.id)}
                                size="small"
                                fullWidth
                                sx={inputSx}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                name="Organizer"
                                value={row.Organizer}
                                onChange={(e) => handleInputChange(e, row.id)}
                                size="small"
                                fullWidth
                                sx={inputSx}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                name="EventType"
                                value={row.EventType}
                                onChange={(e) => handleInputChange(e, row.id)}
                                size="small"
                                fullWidth
                                sx={inputSx}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                name="PhotoURL"
                                value={row.PhotoURL || ""}
                                onChange={(e) => handleInputChange(e, row.id)}
                                size="small"
                                fullWidth
                                sx={inputSx}
                              />
                            </TableCell>

                            <TableCell>
                              <TextField
                                name="SeatsCount"
                                value={row.SeatsCount || ""}
                                onChange={(e) => handleInputChange(e, row.id)}
                                size="small"
                                fullWidth
                                sx={inputSx}
                                type="number"
                              />
                            </TableCell>

                            <TableCell>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                {row.EventFlyer && (
                                  <Tooltip
                                    title={
                                      <Box
                                        component="img"
                                        src={row.EventFlyer.startsWith("http") ? row.EventFlyer : `${API_UPLOAD_URL || API_URL}/uploads/${row.EventFlyer}`}
                                        alt="Flyer Preview Large"
                                        sx={{ maxWidth: 150, maxHeight: 150, objectFit: "contain", borderRadius: 1 }}
                                      />
                                    }
                                    placement="right"
                                    arrow
                                  >
                                    <Box
                                      component="img"
                                      src={row.EventFlyer.startsWith("http") ? row.EventFlyer : `${API_UPLOAD_URL || API_URL}/uploads/${row.EventFlyer}`}
                                      alt="Flyer Preview"
                                      sx={{ width: 60, height: 40, objectFit: "cover", borderRadius: 1, border: "1px solid #ccc", cursor: "pointer" }}
                                    />
                                  </Tooltip>
                                )}
                                <Button
                                  variant="outlined"
                                  component="label"
                                  size="small"
                                  sx={{ textTransform: "none", p: "2px 5px", fontSize: "0.75rem", minWidth: "auto" }}
                                  disabled={uploadingFlyerId === row.id}
                                >
                                  {uploadingFlyerId === row.id ? <CircularProgress size={16} /> : "Upload"}
                                  <input type="file" hidden accept="image/jpeg, image/png, image/jpg" onChange={(e) => handleFlyerUpload(e, row.id)} />
                                </Button>
                              </Box>
                            </TableCell>

                            <TableCell>
                              <IconButton sx={iconBtnSx} onClick={() => handleUpdateRow(row.id)} title="Save">
                                <SaveIcon />
                              </IconButton>
                              <IconButton sx={iconBtnSx} onClick={() => setEditId(null)} title="Cancel">
                                <CancelIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow key={row.id} sx={zebraRowSx(i)}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{row.CampusCode}</TableCell>
                            <TableCell>{row.Subcampus}</TableCell>
                            <TableCell>{row.Title}</TableCell>
                            <TableCell>{row.Description}</TableCell>
                            <TableCell>{row.EventDate?.split("T")[0]}</TableCell>
                            <TableCell>{row.Time?.substr(0, 5)}</TableCell>
                            <TableCell>{row.Location}</TableCell>
                            <TableCell>{row.Organizer}</TableCell>
                            <TableCell>{row.EventType}</TableCell>
                            <TableCell>{row.PhotoURL}</TableCell>
                            <TableCell>{row.SeatsCount}</TableCell>
                            <TableCell>
                              {row.EventFlyer ? (
                                <Tooltip
                                  title={
                                    <Box
                                      component="img"
                                      src={row.EventFlyer.startsWith("http") ? row.EventFlyer : `${API_UPLOAD_URL || API_URL}/uploads/${row.EventFlyer}`}
                                      alt="Flyer Preview Large"
                                      sx={{ maxWidth: 150, maxHeight: 150, objectFit: "contain", borderRadius: 1 }}
                                    />
                                  }
                                  placement="right"
                                  arrow
                                >
                                  <Box
                                    component="img"
                                    src={row.EventFlyer.startsWith("http") ? row.EventFlyer : `${API_UPLOAD_URL || API_URL}/uploads/${row.EventFlyer}`}
                                    alt="Flyer"
                                    sx={{ width: 60, height: 40, objectFit: "cover", borderRadius: 1, border: "1px solid #ccc", cursor: "pointer" }}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography variant="caption" color="textSecondary">None</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton sx={iconBtnSx} onClick={() => setEditId(row.id)} title="Edit">
                                <EditIcon />
                              </IconButton>
                              <IconButton sx={iconBtnSx} onClick={() => handleDeleteRow(row.id)} title="Delete">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )
                      )}

                      {/* new-row form */}
                      <TableRow sx={zebraRowSx(data.length)}>
                        <TableCell />
                        <TableCell>
                          <TextField value={campusCode} disabled fullWidth size="small" sx={inputSx} />
                        </TableCell>

                        <TableCell>
                          <TextField
                            select
                            name="Subcampus"
                            value={newRow.Subcampus || newRow.SubCampus || ""}
                            onChange={(e) => handleInputChange(e)}
                            SelectProps={{ displayEmpty: true }}
                            fullWidth
                            size="small"
                            sx={inputSx}
                          >

                            <MenuItem value="K1">K1</MenuItem>
                            <MenuItem value="KK">KK</MenuItem>
                            <MenuItem value="KW">KW</MenuItem>
                          </TextField>
                        </TableCell>

                        <TableCell>
                          <TextField
                            name="Title"
                            value={newRow.Title}
                            onChange={handleInputChange}
                            placeholder="Title"
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            name="Description"
                            value={newRow.Description}
                            onChange={handleInputChange}
                            placeholder="Description"
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            sx={inputSx}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            type="date"
                            name="EventDate"
                            value={newRow.EventDate}
                            onChange={handleInputChange}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                        </TableCell>

                        <TableCell>
                          <TimePicker
                            value={timeStrToDayjs(newRow.Time)}
                            onChange={(val) => {
                              const timeStr = dayjsToTimeStr(val);
                              setNewRow((r) => ({ ...r, Time: timeStr }));
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                                sx: inputSx,
                                placeholder: "Time",
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            name="Location"
                            value={newRow.Location}
                            onChange={handleInputChange}
                            placeholder="Location"
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            name="Organizer"
                            value={newRow.Organizer}
                            onChange={handleInputChange}
                            placeholder="Organizer"
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            name="EventType"
                            value={newRow.EventType}
                            onChange={handleInputChange}
                            placeholder="EventType"
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            name="PhotoURL"
                            value={newRow.PhotoURL}
                            onChange={handleInputChange}
                            placeholder="Photo URL"
                            fullWidth
                            size="small"
                            sx={inputSx}
                          />
                        </TableCell>

                        <TableCell>
                          <TextField
                            name="SeatsCount"
                            value={newRow.SeatsCount}
                            onChange={handleInputChange}
                            placeholder="Seats Count"
                            fullWidth
                            size="small"
                            sx={inputSx}
                            type="number"
                          />
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            {newRow.EventFlyer && (
                              <Tooltip
                                title={
                                  <Box
                                    component="img"
                                    src={newRow.EventFlyer.startsWith("http") ? newRow.EventFlyer : `${API_UPLOAD_URL || API_URL}/uploads/${newRow.EventFlyer}`}
                                    alt="Flyer Preview Large"
                                    sx={{ maxWidth: 150, maxHeight: 150, objectFit: "contain", borderRadius: 1 }}
                                  />
                                }
                                placement="right"
                                arrow
                              >
                                <Box
                                  component="img"
                                  src={newRow.EventFlyer.startsWith("http") ? newRow.EventFlyer : `${API_UPLOAD_URL || API_URL}/uploads/${newRow.EventFlyer}`}
                                  alt="Flyer Preview"
                                  sx={{ width: 60, height: 40, objectFit: "cover", borderRadius: 1, border: "1px solid #ccc", cursor: "pointer" }}
                                />
                              </Tooltip>
                            )}
                            <Button
                              variant="outlined"
                              component="label"
                              size="small"
                              sx={{ textTransform: "none", p: "2px 5px", fontSize: "0.75rem", minWidth: "auto" }}
                              disabled={uploadingFlyerId === "new"}
                            >
                              {uploadingFlyerId === "new" ? <CircularProgress size={16} /> : "Upload"}
                              <input type="file" hidden accept="image/jpeg, image/png, image/jpg" onChange={(e) => handleFlyerUpload(e, "new")} />
                            </Button>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <IconButton sx={iconBtnSx} onClick={handleAddRow} title="Add">
                            <AddIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>

            <Divider sx={{ mt: 2, opacity: 0 }} />
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default CampusEvents;
